import { useState } from "react";
import { Link } from "react-router-dom";
import { Trees, Landmark, GraduationCap, HeartPulse, ShieldCheck, BadgeCheck, Send, ArrowRight, Phone } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Reveal, SectionHeading } from "@/components/motion";
import { INSTITUTION_QUOTE } from "@/data/testimonials";

const inputCls = "w-full border border-girder bg-steel px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:border-hivis focus:outline-none";
const labelCls = "mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-slate-500";

const USE_CASES = [
  { icon: Trees, t: "Parks & Recreation", d: "Trailhead signage, event banners, field and facility markers that stay readable through spring storms." },
  { icon: Landmark, t: "City & County Government", d: "Civic messaging, wayfinding and event promotion on frames your facilities crew can install and maintain." },
  { icon: GraduationCap, t: "Schools & Universities", d: "Campus announcements, athletics and enrollment banners with a uniform, professional presentation." },
  { icon: HeartPulse, t: "Hospitals & Medical", d: "Campus directional and community-health messaging that won't sag or shred between service visits." },
];

const PROOF = [
  { k: "U.S. Patent", v: "10,410,553 B2" },
  { k: "Origin", v: "100% Made in USA" },
  { k: "Wind Rating", v: "Proven 40+ MPH" },
  { k: "Guarantee", v: "Cannot Collapse" },
];

const SPECS = [
  ["Frame sizes", "4 ft · 6-8 ft · 12-16 ft kits; V-Config holds two banners back-to-back"],
  ["Construction", "Telescoping aluminum bar, T-clips, UV-protected covers, precision-sized bungees"],
  ["Posts", "Standard T-posts sourced locally — no proprietary posts to ship"],
  ["Install", "About 15 minutes, one person, no special tools"],
  ["Maintenance", "Replaceable wear parts (bungees, clips, covers) in stock, ships from the USA"],
  ["Compliance", "U.S. Patent 10,410,553 B2 · Made in USA — supports Buy America preferences"],
];

export default function InstitutionsPage() {
  const [form, setForm] = useState({ name: "", organization: "", org_type: "City / County Government", email: "", phone: "", message: "", hp: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/institutions/quote", form);
      toast.success("Quote request received", { description: "We reply within one business day. Purchase orders welcome." });
      setForm({ name: "", organization: "", org_type: "City / County Government", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSending(false); }
  };

  return (
    <div data-testid="institutions-page">
      <PageHeader
        kicker="Cities · Schools · Healthcare"
        title="Spec-ready banner displays for public spaces."
        sub="The patented, wind-resistant Banner Bar system for parks & recreation, municipal buildings, campuses and medical facilities. Made in the USA, installed by your own crew in about 15 minutes, and guaranteed not to collapse. Purchase orders welcome."
        image="/assets/dealer/threepost-lifestyle-black.jpg"
      />

      {/* USE CASES */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-24" data-testid="institutions-use-cases">
        <SectionHeading kicker="Where It Works" title="Built for the spaces you maintain" />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {USE_CASES.map((c, i) => (
            <Reveal key={c.t} i={i} className="clip-corner border border-girder bg-steel/50 p-7">
              <c.icon className="h-7 w-7 text-hivis" />
              <h3 className="mt-4 font-display text-xl font-bold uppercase text-slate-100">{c.t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PROOF + SPECS */}
      <section className="border-y border-girder bg-steel/40 py-16 lg:py-24" data-testid="institutions-proof">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-2 gap-px border border-girder bg-girder lg:grid-cols-4">
            {PROOF.map((p) => (
              <div key={p.k} className="bg-obsidian/80 px-5 py-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{p.k}</p>
                <p className="mt-1 font-display text-lg font-bold uppercase text-slate-100">{p.v}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 grid gap-12 lg:grid-cols-2">
            <div>
              <SectionHeading kicker="Procurement Ready" title="Specifications for your file" />
              <Reveal i={2}>
                <dl className="mt-8 divide-y divide-girder border-y border-girder" data-testid="institutions-spec-table">
                  {SPECS.map(([k, v]) => (
                    <div key={k} className="grid gap-1 py-3.5 sm:grid-cols-3 sm:gap-4">
                      <dt className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{k}</dt>
                      <dd className="text-sm text-slate-300 sm:col-span-2">{v}</dd>
                    </div>
                  ))}
                </dl>
              </Reveal>
            </div>
            <div>
              <Reveal i={1}>
                <figure className="clip-corner border border-girder bg-obsidian p-8" data-testid="institutions-quote">
                  <BadgeCheck className="h-6 w-6 text-hivis" />
                  <blockquote className="mt-4 text-sm leading-relaxed text-slate-300">"{INSTITUTION_QUOTE.quote}"</blockquote>
                  <figcaption className="mt-5 border-t border-girder pt-4">
                    <p className="font-display text-sm font-bold uppercase tracking-wide text-slate-100">{INSTITUTION_QUOTE.name}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{INSTITUTION_QUOTE.role} · {INSTITUTION_QUOTE.company}</p>
                  </figcaption>
                </figure>
              </Reveal>
              <Reveal i={2}>
                <div className="mt-6 flex items-start gap-3 border border-girder bg-steel/40 p-6">
                  <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-hivis" />
                  <p className="text-sm leading-relaxed text-slate-400">
                    Guaranteed to increase the life of your banner when installed correctly — the durability story your budget committee wants to hear.
                    <Link to="/quality-guarantee" data-testid="institutions-guarantee-link" className="ml-2 whitespace-nowrap font-mono text-xs uppercase tracking-widest text-hivis underline decoration-hivis/50 underline-offset-4 hover:text-hivis-hover">Read the guarantee</Link>
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE FORM */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-24" data-testid="institutions-quote-section">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <SectionHeading kicker="Request a Quote" title="Tell us about your project" />
            <Reveal i={2}>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-slate-400 md:text-base">
                Share a few details and we'll come back with kit recommendations and pricing — usually within one business day. Purchase orders welcome. Prefer to talk? Call <a href="tel:9706505173" className="text-hivis">970-650-5173</a> (8am-5pm MST).
              </p>
            </Reveal>
            <Reveal i={3}>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-slate-400">
                Need a single frame today? You can also order direct.
              </p>
              <Link to="/products" data-testid="institutions-shop-link" className="group mt-4 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-hivis">
                Order direct <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <Reveal i={1}>
            <form onSubmit={submit} className="clip-corner border border-girder bg-obsidian p-6 sm:p-8" data-testid="institutions-quote-form">
              {/* Honeypot: invisible to humans; bots that fill it are silently dropped server-side */}
              <input type="text" name="hp" value={form.hp} onChange={set("hp")} tabIndex={-1} autoComplete="off" aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }} data-testid="hp-field" />
              <div className="grid gap-4 sm:grid-cols-2">
                <div><label className={labelCls} htmlFor="iq-name">Your name *</label><input id="iq-name" required value={form.name} onChange={set("name")} className={inputCls} data-testid="iq-name" placeholder="Jordan Smith" /></div>
                <div><label className={labelCls} htmlFor="iq-org">Organization *</label><input id="iq-org" required value={form.organization} onChange={set("organization")} className={inputCls} data-testid="iq-org" placeholder="City of Montrose" /></div>
                <div><label className={labelCls} htmlFor="iq-email">Work email *</label><input id="iq-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="iq-email" placeholder="jordan@city.gov" /></div>
                <div><label className={labelCls} htmlFor="iq-phone">Phone</label><input id="iq-phone" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="iq-phone" placeholder="(555) 555-0100" /></div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="iq-type">Organization type</label>
                  <select id="iq-type" value={form.org_type} onChange={set("org_type")} className={inputCls} data-testid="iq-type">
                    <option>City / County Government</option>
                    <option>Parks &amp; Recreation</option>
                    <option>School / University</option>
                    <option>Hospital / Medical</option>
                    <option>Other Public Organization</option>
                  </select>
                </div>
              </div>
              <div className="mt-4"><label className={labelCls} htmlFor="iq-message">Project details</label><textarea id="iq-message" rows={3} value={form.message} onChange={set("message")} className={inputCls} data-testid="iq-message" placeholder="Locations, banner sizes, timeline, bid requirements..." /></div>
              <button type="submit" disabled={sending} data-testid="institutions-quote-submit" className="clip-corner mt-6 flex w-full items-center justify-center gap-3 bg-hivis py-4 font-display text-lg font-bold uppercase tracking-wide text-obsidian transition-colors hover:bg-hivis-hover disabled:opacity-60">
                {sending ? "Sending..." : "Request a Quote"} <Send className="h-4 w-4" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
